import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ctorMap } from '../../apps';
import { AppName, useAppDefinitions } from '../../apps/definition';
import { AppState } from '../../constants';
import { trackEvent } from '../../extra/sharedMethods';
import { useAppConnectionStatus } from '../../pages/onboarding/WorkspaceSetupPage/hooks/app-connection-summary';
import { invokeFastApi } from '../../scripts/apis/fastapi';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import { useToaster } from '../../scripts/hooks';
import { logError } from '../../scripts/utils';
import { AppsFilterSwitch } from '../AppsFilterSwitch/AppsFilterSwitch';
import { UIButton } from '../controls/ui/UIButton/UIButton';
import { UITextArea } from '../controls/ui/UITextArea/UITextArea';
import { UITextbox } from '../controls/ui/UITextbox/UITextbox';
import { ConnectedApps } from '../jit-qa/QATextInputView';
import { Tabs, TabsList, TabsTrigger } from '../shadcn/lib/components/tabs';
import {
  BasePromptTemplateFormData,
  validateFormData,
} from './EditPromptTemplate';
import { IInnerComponent } from './QAPromptTemplateModal';

interface ICreatePromptTemplate extends IInnerComponent {
  searchFilterStrings?: AppName[];
  template?: string;
}

export const CreatePromptTemplate: React.FC<ICreatePromptTemplate> = ({
  onClose,
  searchFilterStrings,
  template,
}: ICreatePromptTemplate) => {
  const [formData, setFormData] = useState<BasePromptTemplateFormData>({
    title: '',
    description: '',
    visibility: 'PUBLIC',
    template: template ?? '',
  });

  const [searchFilters, setSearchFilters] = useState<ConnectedApps[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBaseLLM, setIsBaseLLM] = useState(false);

  const toaster = useToaster();
  const definitions = useAppDefinitions();
  const connectedApps = useAppConnectionStatus();
  const sortedInstantConnectedApps = useMemo(() => {
    const instantConnectedApps = connectedApps.reduce(
      (acc: ConnectedApps[], curr) => {
        if (curr.isInstant && curr.statusCode !== AppState.ErrorReconnect) {
          acc.push({ appName: curr.appName, displayName: curr.displayName });
        }

        return acc;
      },
      []
    );

    const uniqueConnectedApps = instantConnectedApps.filter(
      (app, index, self) =>
        index === self.findIndex((t) => t.appName === app.appName)
    );

    uniqueConnectedApps.sort((appA, appB) => {
      return appA.appName.localeCompare(appB.appName);
    });

    return uniqueConnectedApps;
  }, [connectedApps]);

  const handleInputChange = useCallback(
    (name: keyof BasePromptTemplateFormData, value: string) => {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    const { isValid, error } = validateFormData(formData);
    if (isValid) {
      setIsSubmitting(true);

      let searchFiltersForRequest = searchFilters.map((app) => app.appName);

      if (searchFilters.length === 0) {
        searchFiltersForRequest = sortedInstantConnectedApps.map(
          (app) => app.appName
        );
      }

      if (isBaseLLM) {
        searchFiltersForRequest = ['baseLlm'];
      }

      try {
        const bodyData = {
          ...formData,
          search_filters: searchFiltersForRequest,
        };

        await invokeFastApi({
          path: '/prompts/create',
          method: 'POST',
          body: bodyData,
        });

        toaster.success('Workflow created successfully');
      } catch (fetchError) {
        logError(fetchError);
        toaster.failure('There was an error creating the workflow');
      }

      trackEvent(AnalyticsEvent.WorkflowCreated, {
        searchFiltersForRequest,
        formData,
      });
    } else {
      toaster.failure(error);
    }

    setIsSubmitting(false);

    onClose();
  }, [
    formData,
    isBaseLLM,
    onClose,
    searchFilters,
    sortedInstantConnectedApps,
    toaster,
  ]);

  useEffect(() => {
    if (searchFilterStrings) {
      const searchFiltersFromPrompt: ConnectedApps[] =
        searchFilterStrings.reduce((acc: ConnectedApps[], appName) => {
          const definition = definitions[appName];
          const AppConstructor = ctorMap.get(appName);
          if (definition && AppConstructor) {
            const newApp = new AppConstructor(definition);
            acc.push({
              appName: newApp.definition.shortname,
              displayName: newApp.definition.displayName,
            });
          }

          return acc;
        }, []);

      if (searchFilterStrings[0] === 'web') {
        setSearchFilters([{ appName: 'web', displayName: 'Web' }]);
      } else if (searchFilterStrings.length === 0) {
        setIsBaseLLM(true);
        setSearchFilters([]);
      } else {
        setSearchFilters(searchFiltersFromPrompt);
      }
    }
  }, [definitions, searchFilterStrings]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <div>
        <label>Title</label>
        <UITextbox
          className="mt-2"
          maxLength={60}
          onChange={(value) => {
            handleInputChange('title', value);
          }}
          placeholder="Enter title"
          type="text"
          value={formData.title}
        />
      </div>

      <div>
        <label>Description</label>
        <UITextbox
          className="mt-2"
          onChange={(value) => {
            handleInputChange('description', value);
          }}
          placeholder="Enter description"
          type="text"
          value={formData.description ?? ''}
        />
      </div>
      <div className="flex gap-16">
        <div>
          <label>Visibility</label>
          <Tabs className="mt-2" value={formData.visibility}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                onClick={() => {
                  handleInputChange('visibility', 'PRIVATE');
                }}
                value="PRIVATE"
              >
                Private
              </TabsTrigger>
              <TabsTrigger
                onClick={() => {
                  handleInputChange('visibility', 'PUBLIC');
                }}
                value="PUBLIC"
              >
                Organization
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div>
          <label>Sources</label>
          <div className="mt-2">
            <AppsFilterSwitch
              isBaseLLM={isBaseLLM}
              searchFilters={searchFilters}
              setIsBaseLLM={setIsBaseLLM}
              setSearchFilters={setSearchFilters}
              sortedInstantConnectedApps={sortedInstantConnectedApps}
            />
          </div>
        </div>
      </div>

      <div>
        <label>Preview</label>
        <UITextArea
          className="mt-2"
          maxRows={6}
          multilineExpandable
          onChange={(value) => {
            handleInputChange('template', value);
          }}
          value={formData.template}
        />
        <div className="mt-1 text-smoke-10 text-xs">
          {
            'Tip: Change specific keywords into generic keywords for ease of use. Eg. Pizza > Food Name'
          }
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 flex justify-between p-6">
        <UIButton onClick={onClose} type="secondary">
          Cancel
        </UIButton>
        <UIButton onClick={handleSubmit} processing={isSubmitting}>
          Save
        </UIButton>
      </div>
    </form>
  );
};
